import { JobSubmitState, RecordRefusal } from "@gigsync/protocol";
import { log } from "../log.js";
import { HttpError } from "../net/errors.js";
import { Collection } from "../ports.js";
import { lostAgreement, recordsAgreed } from "./consent.js";
import { fingerprint, ReportKind, recordsDue } from "./due.js";
/**
 * Answers that mean the server will never take this fact as it is: a bad body, a signature no active device of the
 * owner's verifies, a job stored with other fields, not the owner's to send, or a job already ended otherwise.
 */
const BAD_REQUEST = 400;
const R = RecordRefusal.enum;
const REFUSALS = new Set([
    R.bad_signature,
    R.record_differs,
    R.households_only,
    R.workers_only,
    R.wrong_status,
]);
const refusedForGood = (err) => err.status === BAD_REQUEST || REFUSALS.has(err.error);
/** What happened to one fact: taken, refused for good, or to try again later (and the job's later facts with it). */
const Outcome = { taken: "taken", refused: "refused", later: "later" };
const ORDER = [ReportKind.job, ReportKind.status, ReportKind.payment, ReportKind.stars];
/**
 * The facts still to send: owed now (`recordsDue`) and not yet answered by the server as they are. A fact whose content
 * changed since (a payment confirmed after it was paid, stars changed) is due again and replaces the server's.
 */
export async function pendingRecords(storage, jobs, payments) {
    const due = recordsDue(jobs, payments);
    const answered = await Promise.all(due.map((r) => storage.get(Collection.recordsSent, r.id)));
    const rows = due.map((report, i) => ({
        report,
        answer: answered[i],
        current: answered[i]?.fingerprint === fingerprint(report),
    }));
    // A job the server will never store, as it stands: nothing that follows it goes either.
    const refusedJobs = new Set(rows
        .filter((row) => row.report.kind === ReportKind.job && row.current && row.answer?.refused)
        .map((row) => row.report.engagement));
    return rows.filter((row) => !row.current && !refusedJobs.has(row.report.engagement)).map((row) => row.report);
}
async function sendOne(api, r) {
    switch (r.kind) {
        case ReportKind.job: {
            const { state } = await api.submitJob(r.body);
            // Kept aside by the server, never counted: the two phones hold different facts (logged to find out why).
            if (state === JobSubmitState.enum.mismatch)
                log.warn("job record differs from the other side's", { engagement: r.engagement });
            return;
        }
        case ReportKind.status:
            return api.reportStatus(r.engagement, r.body);
        case ReportKind.payment:
            return api.reportPayment(r.engagement, r.body);
        case ReportKind.stars:
            return api.reportStars(r.engagement, r.body);
    }
}
async function attempt(deps, r) {
    try {
        await sendOne(deps.api, r);
        await deps.storage.put(Collection.recordsSent, r.id, { fingerprint: fingerprint(r) });
        return Outcome.taken;
    }
    catch (err) {
        if (err instanceof HttpError && err.error === R.no_consent) {
            // The server holds no current agreement: deleted on another phone, or a newer notice.
            await lostAgreement(deps.api, deps.storage);
            return Outcome.later;
        }
        if (err instanceof HttpError && refusedForGood(err)) {
            log.warn("job record refused", { kind: r.kind, engagement: r.engagement, error: err.error });
            await deps.storage.put(Collection.recordsSent, r.id, { fingerprint: fingerprint(r), refused: true });
            return Outcome.refused;
        }
        // Offline, busy, or the job not stored yet because the other phone has not sent it: next sync.
        log.info("job record not sent yet", { kind: r.kind, error: err instanceof Error ? err.message : String(err) });
        return Outcome.later;
    }
}
/** Sends one job's facts in order, stopping at the first to try later: what follows needs it there first. */
async function sendJob(deps, reports) {
    let taken = 0;
    for (const r of [...reports].sort((a, b) => ORDER.indexOf(a.kind) - ORDER.indexOf(b.kind))) {
        const outcome = await attempt(deps, r);
        if (outcome === Outcome.later)
            return { taken, later: true };
        // A job the server will never store has no status, payments or stars to follow it.
        if (outcome === Outcome.refused && r.kind === ReportKind.job)
            break;
        if (outcome === Outcome.taken)
            taken += 1;
    }
    return { taken, later: false };
}
/**
 * After a sync, or right after the owner signs, pays or rates: sends what is pending, once the owner agreed to the
 * notice. A fact that is not taken stays pending and goes again on the next sync. Never throws.
 */
export async function sendRecords(deps, jobs, payments) {
    if (!(await recordsAgreed(deps.storage)))
        return { taken: 0, later: false };
    const pending = await pendingRecords(deps.storage, jobs, payments);
    const byJob = new Map();
    for (const r of pending)
        byJob.set(r.engagement, [...(byJob.get(r.engagement) ?? []), r]);
    const results = await Promise.all([...byJob.values()].map((reports) => sendJob(deps, reports)));
    return { taken: results.reduce((n, r) => n + r.taken, 0), later: results.some((r) => r.later) };
}
/** After the owner's records were deleted: everything counts as unsent, for if they agree again. */
export async function forgetSentRecords(storage) {
    const ids = await storage.ids(Collection.recordsSent);
    await Promise.all(ids.map((id) => storage.delete(Collection.recordsSent, id)));
}
//# sourceMappingURL=send.js.map